import { create } from "zustand";
import marketdata from "../../data/Services/marketdata";
import { parseInterval } from "../chart/chartDataProcessor";
import { mergeComparisonQuoteInPlace } from "./compareTransform";

// Default colors for comparison symbols (cycling palette)
const COMPARE_COLORS = [
  "#2196F3", // Blue
  "#FF9800", // Orange
  "#4CAF50", // Green
  "#E91E63", // Pink
  "#9C27B0", // Purple
  "#00BCD4", // Cyan
  "#FF5722", // Deep Orange
  "#795548", // Brown
];

// Initial state for compare store
const initialCompareState = {
  // Map of chartId -> comparison data
  // { [chartId]: { symbols: [{ symbol, data, color, isLoading, error }], periodicity } }
  comparisons: {},
  // Track which symbols are subscribed and which charts use them
  // symbol -> Set<chartId>
  subscribedSymbols: {},
  // Data version counter to trigger re-renders (incremented on quote updates)
  dataVersion: 0,
  // Flag to track if receiver is registered
  receiverRegistered: false,
};

export const useCompareStore = create((set, get) => ({
  ...initialCompareState,

  // Check if a chart is in compare mode (has comparison symbols)
  isCompareMode: (chartId) => {
    const comparison = get().comparisons[chartId];
    return comparison?.symbols?.length > 0;
  },

  // Get all comparison symbols for a chart
  getComparisonSymbols: (chartId) => {
    const comparison = get().comparisons[chartId];
    return comparison?.symbols || [];
  },

  // Get next available color for a chart
  getNextColor: (chartId) => {
    const symbols = get().getComparisonSymbols(chartId);
    const usedColors = symbols.map((s) => s.color);
    const availableColor = COMPARE_COLORS.find((c) => !usedColors.includes(c));
    return (
      availableColor || COMPARE_COLORS[symbols.length % COMPARE_COLORS.length]
    );
  },

  // Add a comparison symbol to a chart
  // Optional preferredColor parameter allows restoring saved colors when loading workspaces
  addComparisonSymbol: async (
    chartId,
    symbol,
    mainChartConfig,
    preferredColor = null,
  ) => {
    const { periodicity } = mainChartConfig;
    const existingSymbols = get().getComparisonSymbols(chartId);

    // Check if symbol already exists
    if (existingSymbols.some((s) => s.symbol === symbol)) {
      console.warn(
        `[CompareStore] Symbol ${symbol} already added to chart ${chartId}`,
      );
      return false;
    }

    // Use preferred color if provided, otherwise get next available color
    const color = preferredColor || get().getNextColor(chartId);

    // Add symbol with loading state and store periodicity
    set((state) => ({
      comparisons: {
        ...state.comparisons,
        [chartId]: {
          symbols: [
            ...(state.comparisons[chartId]?.symbols || []),
            { symbol, color, data: [], isLoading: true, error: null },
          ],
          periodicity, // Store periodicity for real-time updates
        },
      },
    }));

    try {
      // Parse periodicity (e.g., "1min" -> period: "1", interval: "min" -> parsedInterval: "minute")
      const period = periodicity.match(/\d+/g)?.[0] || "1";
      const interval = periodicity.match(/[a-zA-Z]+/g)?.[0] || "min";
      const parsedInterval = parseInterval(interval);

      // Set date range
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 1);
      const startDate = new Date();
      startDate.setFullYear(startDate.getFullYear() - 10);

      // Fetch historical data for the comparison symbol
      const result = await marketdata.getBars(symbol, startDate, endDate, {
        periodicity: parsedInterval,
        interval: period,
        barscount: 4000,
        aftermarket: false,
        adjusted: true,
      });

      if (result?.bars) {
        // Convert API bars to internal format with Date objects
        const formattedBars = result.bars.map((bar) => ({
          date: new Date(bar.T * 1000),
          open: bar.O,
          high: bar.H,
          low: bar.L,
          close: bar.C,
          volume: bar.V,
        }));

        // Update with fetched data
        set((state) => ({
          comparisons: {
            ...state.comparisons,
            [chartId]: {
              ...state.comparisons[chartId],
              symbols: state.comparisons[chartId].symbols.map((s) =>
                s.symbol === symbol
                  ? { ...s, data: formattedBars, isLoading: false }
                  : s,
              ),
            },
          },
        }));

        // Register for real-time quotes after data is loaded
        get().registerComparisonQuote(chartId, symbol);

        return true;
      } else {
        throw new Error("No data returned");
      }
    } catch (error) {
      console.error(`[CompareStore] Error fetching data for ${symbol}:`, error);

      // Update with error state
      set((state) => ({
        comparisons: {
          ...state.comparisons,
          [chartId]: {
            ...state.comparisons[chartId],
            symbols: state.comparisons[chartId].symbols.map((s) =>
              s.symbol === symbol
                ? { ...s, isLoading: false, error: error.message }
                : s,
            ),
          },
        },
      }));
      return false;
    }
  },

  // Remove a comparison symbol from a chart
  removeComparisonSymbol: (chartId, symbol) => {
    // Unregister from real-time quotes first
    get().unregisterComparisonQuote(chartId, symbol);

    set((state) => {
      const currentSymbols = state.comparisons[chartId]?.symbols || [];
      const newSymbols = currentSymbols.filter((s) => s.symbol !== symbol);

      // If no symbols left, remove the chart entry entirely
      if (newSymbols.length === 0) {
        const { [chartId]: _, ...restComparisons } = state.comparisons;
        return { comparisons: restComparisons };
      }

      return {
        comparisons: {
          ...state.comparisons,
          [chartId]: {
            ...state.comparisons[chartId],
            symbols: newSymbols,
          },
        },
      };
    });
  },

  // Clear all comparison symbols for a chart
  clearComparisons: (chartId) => {
    // Unregister all comparison symbols for this chart
    const symbols = get().getComparisonSymbols(chartId);
    for (const { symbol } of symbols) {
      get().unregisterComparisonQuote(chartId, symbol);
    }

    set((state) => {
      const { [chartId]: _, ...restComparisons } = state.comparisons;
      return { comparisons: restComparisons };
    });
  },

  // Update comparison symbol data (for real-time updates)
  updateComparisonData: (chartId, symbol, newData) => {
    set((state) => ({
      comparisons: {
        ...state.comparisons,
        [chartId]: {
          ...state.comparisons[chartId],
          symbols:
            state.comparisons[chartId]?.symbols.map((s) =>
              s.symbol === symbol ? { ...s, data: newData } : s,
            ) || [],
        },
      },
    }));
  },

  // ============================================================================
  // REAL-TIME QUOTE HANDLING
  // ============================================================================

  /**
   * Initialize the quote receiver with marketdata service.
   * This should be called once when the app starts.
   */
  initializeReceiver: () => {
    if (get().receiverRegistered) return;

    marketdata.registerReceiver({
      OnQuote: get().onQuote,
    });

    set({ receiverRegistered: true });
    console.log("[CompareStore] 📡 Registered as quote receiver");
  },

  /**
   * Handle incoming quote from marketdata service.
   * Finds matching comparison symbols and updates their data in-place.
   * @param {Object} quote - Quote object { S, DT, O, H, L, C, Q, F, TC }
   */
  onQuote: (quote) => {
    // Ignore snapshot quotes (TY="H") - comparison charts don't need them
    if (quote.TY === "H") return;

    const { comparisons, subscribedSymbols } = get();

    // Quick check: is this symbol being compared anywhere?
    const quoteSymbol = quote.S;
    let symbolMatched = false;

    // Check direct match
    if (subscribedSymbols[quoteSymbol]) {
      symbolMatched = true;
    }

    // Check family match (WIN/WDO consolidation)
    if (!symbolMatched) {
      for (const subscribedSymbol of Object.keys(subscribedSymbols)) {
        if (marketdata.isSameFamily(subscribedSymbol, quoteSymbol)) {
          symbolMatched = true;
          break;
        }
      }
    }

    if (!symbolMatched) return;

    // Process quote for all relevant charts
    let anyUpdated = false;

    for (const [, chartComparison] of Object.entries(comparisons)) {
      if (!chartComparison?.symbols) continue;

      const { periodicity } = chartComparison;
      if (!periodicity) continue;

      for (const symbolData of chartComparison.symbols) {
        // Check if this quote matches the comparison symbol
        const matches =
          symbolData.symbol === quoteSymbol ||
          marketdata.isSameFamily(symbolData.symbol, quoteSymbol);

        if (
          matches &&
          symbolData.data &&
          symbolData.data.length > 0 &&
          !symbolData.isLoading
        ) {
          // Merge quote into comparison data in-place
          const changeType = mergeComparisonQuoteInPlace(
            symbolData.data,
            quote,
            periodicity,
          );

          if (changeType) {
            anyUpdated = true;
          }
        }
      }
    }

    // Increment dataVersion to trigger re-renders if any data was updated
    if (anyUpdated) {
      set((state) => ({
        dataVersion: state.dataVersion + 1,
      }));
    }
  },

  /**
   * Register a comparison symbol for real-time quotes.
   * Uses reference counting to avoid duplicate subscriptions.
   * @param {string} chartId - Chart ID
   * @param {string} symbol - Symbol to register
   */
  registerComparisonQuote: (chartId, symbol) => {
    // Ensure receiver is initialized
    get().initializeReceiver();

    set((state) => {
      const newSubscribedSymbols = { ...state.subscribedSymbols };

      // Add this chart to the symbol's subscriber list
      if (!newSubscribedSymbols[symbol]) {
        newSubscribedSymbols[symbol] = new Set();
      }
      newSubscribedSymbols[symbol].add(chartId);

      // Register with marketdata using unique subscriber ID
      // The ref counting in marketdata handles deduplication
      const subscriberId = `compare-${chartId}-${symbol}`;
      console.log(
        `[CompareStore] 📡 Registering quote for comparison: ${symbol} (${subscriberId})`,
      );
      marketdata.registerQuote(symbol, subscriberId);

      return { subscribedSymbols: newSubscribedSymbols };
    });
  },

  /**
   * Unregister a comparison symbol from real-time quotes.
   * Only unregisters from marketdata when no charts are using it.
   * @param {string} chartId - Chart ID
   * @param {string} symbol - Symbol to unregister
   */
  unregisterComparisonQuote: (chartId, symbol) => {
    set((state) => {
      const newSubscribedSymbols = { ...state.subscribedSymbols };

      if (newSubscribedSymbols[symbol]) {
        newSubscribedSymbols[symbol].delete(chartId);

        // Unregister from marketdata with the same subscriber ID
        const subscriberId = `compare-${chartId}-${symbol}`;
        console.log(
          `[CompareStore] 📡 Unregistering quote for comparison: ${symbol} (${subscriberId})`,
        );
        marketdata.unregisterQuote(symbol, subscriberId);

        // Clean up internal tracking if no more charts use this symbol
        if (newSubscribedSymbols[symbol].size === 0) {
          delete newSubscribedSymbols[symbol];
        }
      }

      return { subscribedSymbols: newSubscribedSymbols };
    });
  },

  // Reload comparison data (e.g., after periodicity change)
  reloadComparisonData: async (chartId, mainChartConfig) => {
    const symbols = get().getComparisonSymbols(chartId);
    const { periodicity } = mainChartConfig;

    // Parse periodicity
    const period = periodicity.match(/\d+/g)?.[0] || "1";
    const interval = periodicity.match(/[a-zA-Z]+/g)?.[0] || "min";
    const parsedInterval = parseInterval(interval);

    // Set date range
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + 1);
    const startDate = new Date();
    startDate.setFullYear(startDate.getFullYear() - 10);

    // Update periodicity for this chart's comparisons
    set((state) => ({
      comparisons: {
        ...state.comparisons,
        [chartId]: {
          ...state.comparisons[chartId],
          periodicity,
        },
      },
    }));

    for (const { symbol } of symbols) {
      // Set loading state
      set((state) => ({
        comparisons: {
          ...state.comparisons,
          [chartId]: {
            ...state.comparisons[chartId],
            symbols: state.comparisons[chartId].symbols.map((s) =>
              s.symbol === symbol ? { ...s, isLoading: true, error: null } : s,
            ),
          },
        },
      }));

      try {
        const result = await marketdata.getBars(symbol, startDate, endDate, {
          periodicity: parsedInterval,
          interval: period,
          barscount: 4000,
          aftermarket: false,
          adjusted: true,
        });

        if (result?.bars) {
          // Convert API bars to internal format with Date objects
          const formattedBars = result.bars.map((bar) => ({
            date: new Date(bar.T * 1000),
            open: bar.O,
            high: bar.H,
            low: bar.L,
            close: bar.C,
            volume: bar.V,
          }));

          set((state) => ({
            comparisons: {
              ...state.comparisons,
              [chartId]: {
                ...state.comparisons[chartId],
                symbols: state.comparisons[chartId].symbols.map((s) =>
                  s.symbol === symbol
                    ? { ...s, data: formattedBars, isLoading: false }
                    : s,
                ),
              },
            },
          }));
        }
      } catch (error) {
        console.error(
          `[CompareStore] Error reloading data for ${symbol}:`,
          error,
        );
        set((state) => ({
          comparisons: {
            ...state.comparisons,
            [chartId]: {
              ...state.comparisons[chartId],
              symbols: state.comparisons[chartId].symbols.map((s) =>
                s.symbol === symbol
                  ? { ...s, isLoading: false, error: error.message }
                  : s,
              ),
            },
          },
        }));
      }
    }
  },
}));

// Expose store on window for debugging
if (typeof window !== "undefined") {
  window.__COMPARE_STORE__ = useCompareStore.getState();
  useCompareStore.subscribe((state) => {
    window.__COMPARE_STORE__ = state;
  });
}
